// Lead capture — submits to the site's own backend (POST /api/lead), which
// forwards to GoHighLevel's Contacts API server-side.
//
// Why no direct GHL form POST anymore: GHL's public form endpoint is gated
// behind reCAPTCHA Enterprise, which is blocked in Facebook/Instagram in-app
// browsers — where nearly all paid traffic lands — silently killing every
// lead. The backend route uses the Contacts API (contacts/upsert) with a
// server-held API key, so no captcha is involved and no credentials ever
// reach the client. See server/index.js.
//
// NO health data and no analytics payload here — attribution params only
// travel to the CRM, never to gtag/fbq.

// Attribution params captured from the URL and persisted for the session so
// they survive SPA soft-navigation (Part 4 of the lead-flow spec).
export const LEAD_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "coupon",
];

const SS_PREFIX = "vy_lead_param_";

/** Read tracked params from the current URL into sessionStorage (idempotent). */
export function captureLeadParams() {
  try {
    const qs = new URLSearchParams(window.location.search);
    LEAD_PARAM_KEYS.forEach((k) => {
      const v = qs.get(k);
      if (v) sessionStorage.setItem(SS_PREFIX + k, v);
    });
  } catch {
    /* sessionStorage unavailable (private mode) — non-fatal */
  }
}

captureLeadParams();

/** Return { key: value } for every tracked param present this session. */
export function getLeadParams() {
  const out = {};
  try {
    LEAD_PARAM_KEYS.forEach((k) => {
      const v = sessionStorage.getItem(SS_PREFIX + k);
      if (v) out[k] = v;
    });
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Read one attribution value from (in order): localStorage `vyvron_<key>`
 * (persisted by the homepage), sessionStorage `vy_lead_param_<key>`, or the
 * current URL. Returns "" when absent — callers submit blanks, never block.
 */
function readAttribution(key) {
  try {
    const ls = localStorage.getItem("vyvron_" + key);
    if (ls) return ls;
  } catch {
    /* private mode — keep looking */
  }
  try {
    const ss = sessionStorage.getItem(SS_PREFIX + key);
    if (ss) return ss;
  } catch {
    /* ignore */
  }
  try {
    return new URLSearchParams(window.location.search).get(key) || "";
  } catch {
    return "";
  }
}

/** Reuse the funnel's session click id for shared email boxes; never invent one. */
function readSessionCid(lane) {
  try {
    const prefix = { mwl: "vy_start_", microdose: "vy_micro_", longevity: "vy_lgv_" }[lane];
    return prefix ? sessionStorage.getItem(prefix + "cid") || "" : "";
  } catch {
    return "";
  }
}

/** Program lane: URL ?lane wins; else caller-provided; else derived from the route. */
function resolveLane(callerLane) {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("lane");
    if (fromUrl) return fromUrl;
  } catch {
    /* ignore */
  }
  if (callerLane) return callerLane;
  try {
    const p = window.location.pathname.replace(/\/$/, "");
    if (p === "/start") return "mwl";
    if (p === "/microdose") return "microdose";
    if (p === "/longevity") return "longevity";
  } catch {
    /* ignore */
  }
  return "";
}

/** Read a cookie value by name; "" when absent or cookies unavailable. */
function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
}

/**
 * Submit a lead via the backend. Resolves on success, throws on failure so
 * callers can render an error state — never a thank-you the site cannot
 * vouch for. Missing attribution values are sent blank; attribution can
 * never block or delay a submit.
 */
export async function submitLead({ firstName, lastName, email, phone, extra, hp, renderedAt, metaEvent = "lead" }) {
  const x = extra || {};
  captureLeadParams();
  const lane = resolveLane(x.lane);
  // Meta events, controlled by `metaEvent`:
  //   "lead" (default) — the primary conversion Meta optimizes toward:
  //     browser pixel "Lead" + backend Conversions API "Lead", deduplicated
  //     via a shared eventId. Fire ONLY at high-intent moments (quiz
  //     completion), never on soft email captures, or Meta optimizes for
  //     email-typers instead of qualified intent.
  //   "soft" — lighter custom pixel event (SoftLead), NO eventId, so the
  //     backend skips the CAPI Lead entirely.
  //   "softOptin" — /start email-only opt-in; custom SoftOptin after a
  //     confirmed save, NO eventId or CAPI Lead.
  //   "none" — no Meta event at all.
  let eventId = "";
  try {
    if (metaEvent === "lead") {
      eventId = crypto.randomUUID();
    } else if (metaEvent === "soft" && typeof window.fbq === "function") {
      window.fbq("trackCustom", "SoftLead");
    }
  } catch {
    /* pixel blocked or crypto unavailable — never blocks the submit */
  }
  const payload = {
    firstName: firstName || "",
    ...(lastName ? { lastName } : {}),
    email,
    phone: phone || "",
    state: x.state || "",
    goal: x.goal || "",
    lane,
    utm_source: readAttribution("utm_source"),
    utm_content: readAttribution("utm_content"),
    utm_campaign: readAttribution("utm_campaign"),
    coupon: readAttribution("coupon"),
    gclid: readAttribution("gclid"),
    cid: x.cid || readSessionCid(lane),
    eventId,
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
    // Bot defense (see src/lib/botDefense.jsx): honeypot decoy value and
    // form render→submit elapsed time. The server silently drops
    // submissions with a filled honeypot or an impossibly fast submit.
    hp: hp || "",
    ...(typeof renderedAt === "number" && renderedAt > 0
      ? { elapsedMs: Date.now() - renderedAt }
      : {}),
  };

  let res;
  try {
    res = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    trackLeadEvent("lead_submit_unverified", { reason: err.message.slice(0, 100) }); // no PII
    throw new Error(`Lead submission could not be verified: ${err.message}`);
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON response — treated as failure below unless res.ok */
  }

  if (res.ok && data && data.ok) {
    // Count confirmed captures only, never attempts or failed submissions.
    // The synchronous base Pixel queues this if fbevents.js is still loading.
    // Share the server's event ID so Pixel + CAPI count as one conversion.
    try {
      if (metaEvent === "lead" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", {}, eventId ? { eventID: eventId } : {});
      } else if (metaEvent === "softOptin" && typeof window.fbq === "function") {
        window.fbq("trackCustom", "SoftOptin");
      }
    } catch {
      /* Tracking failures must not interrupt the successful intake handoff. */
    }
    trackLeadEvent("lead_submit_success");
  } else {
    const reason = (data && data.error) || `HTTP ${res.status}`;
    trackLeadEvent("lead_submit_rejected", {
      http_status: res.status,
      reason: String(reason).slice(0, 100),
    }); // no PII
    throw new Error(`Lead submission rejected: ${reason}`);
  }

  // Session-wide "already captured" flag — keeps every capture point (inline
  // forms, homepage exit modal, /start exit modal) from re-prompting a visitor
  // who already left an email this session.
  try {
    sessionStorage.setItem("vy_lead_captured", "true");
  } catch {
    /* private mode — non-fatal */
  }
}

/**
 * Fire a PII-free analytics event if gtag is present.
 *
 * Events emitted by this module:
 *   lead_submit_success    — lead accepted (backend confirmed GHL upsert)
 *   lead_submit_rejected   — backend or GHL rejected the lead; params:
 *                            http_status, reason
 *   lead_submit_unverified — fetch to /api/lead threw (network down); the
 *                            lead did not reach the CRM; params: reason
 *
 * Set up GA4 Custom Insights on these events — see docs/lead-monitoring.md.
 */
export function trackLeadEvent(name, params = {}) {
  try {
    if (typeof window.gtag === "function") window.gtag("event", name, params);
  } catch {
    /* ignore */
  }
}
